import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';
import { cx } from './cx.js';

// ── Toast ────────────────────────────────────────────────────────────────────

export type Tone = 'info' | 'success' | 'warning' | 'danger';

const TOAST_ICON: Record<Tone, IconName> = { info: 'message', success: 'check', warning: 'alert', danger: 'alert' };

/** Notification brève. Titre au passé composé, texte = la suite ou quoi faire. */
export function Toast({ tone = 'info', title, onClose, children }: {
  tone?: Tone;
  title?: string;
  onClose?: () => void;
  children?: ReactNode;
}) {
  return (
    <div className={cx('pl-toast', `pl-toast-${tone}`)} role={tone === 'danger' ? 'alert' : 'status'}>
      <span className="pl-toast-icon"><Icon name={TOAST_ICON[tone]} size={16} /></span>
      <div>
        {title && <div className="pl-toast-title">{title}</div>}
        {children && <div className="pl-toast-text">{children}</div>}
      </div>
      {onClose && (
        <button type="button" className="pl-toast-close" onClick={onClose} aria-label="Fermer">
          <Icon name="x" size={14} />
        </button>
      )}
    </div>
  );
}

// ── Badge ────────────────────────────────────────────────────────────────────

/** Étiquette courte d'état ou de catégorie. `mono` pour les rôles et compteurs. */
export function Badge({ tone = 'neutral', mono, children }: {
  tone?: 'neutral' | 'primary' | 'success' | 'warning' | 'danger';
  mono?: boolean;
  children?: ReactNode;
}) {
  return <span className={cx('pl-badge', `pl-badge-${tone}`, mono && 'pl-badge-mono')}>{children}</span>;
}

// ── StatusDot ────────────────────────────────────────────────────────────────

export type PresenceStatus = 'online' | 'away' | 'busy' | 'offline';

const STATUS_LABEL: Record<PresenceStatus, string> = {
  online: 'Disponible', away: 'Absent', busy: 'Occupé', offline: 'Hors ligne',
};

/** Pastille de présence. Le libellé reste lu par les lecteurs d'écran même masqué. */
export function StatusDot({ status = 'online', label, showLabel = true }: {
  status?: PresenceStatus;
  label?: string;
  showLabel?: boolean;
}) {
  const text = label ?? STATUS_LABEL[status];
  return (
    <span className="pl-status">
      <span className={cx('pl-status-dot', `pl-status-${status}`)} aria-hidden="true" />
      {showLabel ? text : <span className="pl-sr">{text}</span>}
    </span>
  );
}

// ── Prompt (invite posée sur la carte) ──────────────────────────────────────

/** Invite flottante près d'une porte, d'un bureau ou d'un objet : [touche] verbe. */
export function Prompt({ keyHint, locked, children }: {
  keyHint?: string;
  locked?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className={cx('pl-prompt', locked && 'pl-prompt-locked')}>
      {keyHint && <kbd className="pl-kbd">{keyHint}</kbd>}
      <span>{children}</span>
    </div>
  );
}
